// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessWithdraw.jsx
// Название: Business Withdraw Component - EUR + USDT + Gift Cards
// Версия: 3.0.0
// Дата: 03.12.2025

import React, { useState, useEffect } from 'react';
import './BusinessWithdraw.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const CATEGORIES = [
  { id: 'crypto', icon: '💎', name: 'Криптовалюта', desc: 'Обменять DAR → USDT' },
  { id: 'bank', icon: '🏦', name: 'Банк (EUR)', desc: 'С документами' },
  { id: 'giftcard', icon: '🎁', name: 'Gift Cards', desc: 'Amazon, Steam и др.' },
  { id: 'mobile', icon: '📱', name: 'Мобильный', desc: 'Пополнение телефона' },
  { id: 'gaming', icon: '🎮', name: 'Игры', desc: 'PSN, Xbox, Nintendo' },
];

const BusinessWithdraw = () => {
  const [category, setCategory] = useState('crypto');
  const [method, setMethod] = useState('usdt');
  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [iban, setIban] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [withdrawInfo, setWithdrawInfo] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState('tron');
  const [calculation, setCalculation] = useState(null);
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  
  // Gift Cards
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCategory, setProductCategory] = useState('all');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchWithdrawInfo();
    fetchBalance();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (category === 'giftcard' || category === 'mobile' || category === 'gaming') {
      fetchProducts();
    }
  }, [category]);

  const fetchWithdrawInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crypto/info`);
      const data = await response.json();
      if (data.success) {
        setWithdrawInfo(data.data.withdraw);
        if (data.data.networks?.length > 0) {
          setNetworks(data.data.networks.filter(n => n.enabled));
          setSelectedNetwork(data.data.defaultNetwork || 'tron');
        }
      }
    } catch (err) {
      console.error('Error fetching withdraw info:', err);
    }
  };

  const fetchBalance = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await fetch(`${API_URL}/api/balance/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setBalance(parseFloat(data.data.available_amount || data.data.amount) || 0);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crypto/withdraw/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setHistory(data.data.withdraws || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const cat = category === 'giftcard' ? 'giftcard' : category === 'mobile' ? 'mobile' : 'gaming';
      const response = await fetch(`${API_URL}/api/giftcards/products?category=${cat}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setProducts(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const getCurrentNetwork = () => networks.find(n => n.code === selectedNetwork) || null;

  const getUsdtLimits = () => {
    const network = getCurrentNetwork();
    if (network?.tokens) {
      const usdt = network.tokens.find(t => t.symbol === 'USDT');
      if (usdt?.withdraw) return { min: usdt.withdraw.minAmount || 10, max: usdt.withdraw.maxAmount || 5000 };
    }
    return { min: 10, max: 5000 };
  };

  const calculateUsdt = (usdt) => {
    if (!usdt) return null;
    const platformFee = usdt * 0.01;
    const networkFee = 2;
    const total = usdt + platformFee + networkFee;
    return { amount: usdt.toFixed(2), platformFee: platformFee.toFixed(2), networkFee: networkFee.toFixed(2), totalDeduction: total.toFixed(2), youReceive: usdt.toFixed(2) };
  };

  const calculateEur = (usdt) => {
    if (!withdrawInfo || !usdt) return null;
    const rate = withdrawInfo.currentRates?.rateWithdrawal || 0.91;
    const spread = withdrawInfo.currentRates?.ourMarginPercent || 5;
    const bankFee = 5;
    const eurBeforeMargin = usdt / rate;
    const marginEur = eurBeforeMargin * (spread / 100);
    const finalEur = eurBeforeMargin - marginEur - bankFee;
    return { usdt: usdt.toFixed(2), rate: rate.toFixed(4), spread, marginEur: marginEur.toFixed(2), bankFee, finalEur: finalEur.toFixed(2) };
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setError(null);
    if (!value || isNaN(value)) { setCalculation(null); return; }

    const num = parseFloat(value);
    if (method === 'usdt') {
      const limits = getUsdtLimits();
      const calc = calculateUsdt(num);
      if (num < limits.min) { setError(`Минимум ${limits.min} DAR`); setCalculation(null); return; }
      if (num > limits.max) { setError(`Максимум ${limits.max} DAR`); setCalculation(null); return; }
      if (calc && parseFloat(calc.totalDeduction) > balance) setError(`Недостаточно средств. Доступно: ${balance.toFixed(2)} DAR`);
      setCalculation(calc);
    } else {
      const min = 50, max = 10000;
      if (num < min) { setError(`Минимум ${min} DAR`); setCalculation(null); return; }
      if (num > max) { setError(`Максимум ${max} DAR`); setCalculation(null); return; }
      if (num > balance) setError(`Недостаточно средств. Доступно: ${balance.toFixed(2)} DAR`);
      setCalculation(calculateEur(num));
    }
  };

  const validateAddress = (address) => {
    const network = getCurrentNetwork();
    if (!network || network.type === 'tron') return address?.length === 34 && address.startsWith('T');
    if (network.type === 'evm') return address?.length === 42 && address.startsWith('0x');
    return address?.length > 20;
  };

  const isValidIban = (iban) => iban && /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(iban.replace(/\s/g, ''));

  const getAddressPlaceholder = () => {
    const network = getCurrentNetwork();
    if (!network || network.type === 'tron') return 'T...';
    if (network.type === 'evm') return '0x...';
    return 'Адрес кошелька';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || isNaN(amount)) { setError('Введите сумму'); return; }
    if (method === 'usdt' && !validateAddress(toAddress)) { setError('Введите корректный адрес'); return; }
    if (method === 'eur' && !isValidIban(iban)) { setError('Введите корректный IBAN'); return; }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = method === 'usdt' ? `${API_URL}/api/crypto/withdraw/usdt` : `${API_URL}/api/crypto/withdraw/eur`;
      const body = method === 'usdt' ? { amount: parseFloat(amount), toAddress, network: selectedNetwork } : { amount: parseFloat(amount), iban: iban.replace(/\s/g, '') };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Ошибка создания заявки');

      const result = data.data;
      const networkName = getCurrentNetwork()?.name || selectedNetwork.toUpperCase();
      const successMsg = method === 'usdt'
        ? `✅ Заявка создана!\n${result.autoApproved ? 'Авто-одобрено.' : 'Ожидает одобрения.'}\nСеть: ${networkName}\nСумма: ${result.amount} DAR`
        : `✅ Заявка создана!\nОжидает одобрения.\nСумма к получению: €${result.finalEur}`;

      setSuccess(successMsg);
      setAmount(''); setToAddress(''); setIban(''); setCalculation(null);
      fetchBalance(); fetchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyProduct = async (product) => {
    if (product.price_usdt > balance) {
      setError(`Недостаточно средств. Нужно: ${product.price_usdt} DAR`);
      return;
    }

    if (!window.confirm(`Купить ${product.name} за ${product.price_usdt} DAR?`)) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/giftcards/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ productId: product.id })
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Ошибка заказа');

      setOrderSuccess({
        product: product.name,
        code: data.data.code,
        price: product.price_usdt
      });
      fetchBalance();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Код скопирован!');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-pending', text: '⏳ Ожидает' },
      approved: { class: 'badge-approved', text: '✅ Одобрен' },
      processing: { class: 'badge-processing', text: '⚙️ Обработка' },
      completed: { class: 'badge-success', text: '✅ Выполнен' },
      rejected: { class: 'badge-error', text: '❌ Отклонён' },
      failed: { class: 'badge-error', text: '❌ Ошибка' }
    };
    return badges[status] || { class: 'badge-default', text: status };
  };

  const limits = getUsdtLimits();
  const currentNetwork = getCurrentNetwork();
  const isGiftCategory = category === 'giftcard' || category === 'mobile' || category === 'gaming';

  return (
    <div className="business-withdraw">
      <h2>💸 Вывод средств</h2>

      <div className="balance-card">
        <span className="balance-label">Доступный баланс:</span>
        <span className="balance-amount">{balance.toFixed(2)} DAR</span>
      </div>

      {/* Категории */}
      <div className="category-grid">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`category-btn ${category === cat.id ? 'active' : ''}`}
            onClick={() => { setCategory(cat.id); setMethod(cat.id === 'bank' ? 'eur' : 'usdt'); setError(null); setSuccess(null); setOrderSuccess(null); }}
          >
            <span className="category-icon">{cat.icon}</span>
            <span className="category-name">{cat.name}</span>
            <span className="category-desc">{cat.desc}</span>
          </button>
        ))}
      </div>

      {/* Gift Cards / Mobile / Gaming */}
      {isGiftCategory && (
        <div className="products-section">
          {orderSuccess ? (
            <div className="order-success">
              <div className="success-icon">✅</div>
              <h3>Заказ выполнен!</h3>
              <p className="product-name">{orderSuccess.product}</p>
              <div className="code-block">
                <span className="code-label">Ваш код:</span>
                <code className="code-value">{orderSuccess.code}</code>
                <button className="btn-copy" onClick={() => copyCode(orderSuccess.code)}>📋</button>
              </div>
              <p className="price-paid">Списано: {orderSuccess.price} DAR</p>
              <button className="btn-another" onClick={() => setOrderSuccess(null)}>Купить ещё</button>
            </div>
          ) : loadingProducts ? (
            <div className="loading-products">Загрузка продуктов...</div>
          ) : products.length === 0 ? (
            <div className="no-products">Нет доступных продуктов в этой категории</div>
          ) : (
            <div className="products-grid">
              {products.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-icon">{product.icon || '🎁'}</div>
                  <div className="product-name">{product.name}</div>
                  <div className="product-price">{product.price_usdt} DAR</div>
                  {product.description && <div className="product-desc">{product.description}</div>}
                  <button
                    className="btn-buy"
                    onClick={() => handleBuyProduct(product)}
                    disabled={loading || product.price_usdt > balance}
                  >
                    {product.price_usdt > balance ? 'Недостаточно' : 'Купить'}
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <div className="alert alert-error">{error}</div>}
        </div>
      )}

      {/* Crypto / Bank forms */}
      {!isGiftCategory && (
        <form onSubmit={handleSubmit} className="withdraw-form">
          {category === 'bank' && (
            <>
              <div className="info-box"><h4>🏦 Вывод EUR на банковский счёт</h4><p>Идеально для бизнеса: с документами, подходит для бухгалтерии</p></div>
              <div className="form-group">
                <label>СУММА В DAR для конвертации</label>
                <div className="input-wrapper">
                  <input type="number" value={amount} onChange={handleAmountChange} placeholder="50 - 10000" min="50" max="10000" step="1" disabled={loading} />
                  <span className="currency">USDT</span>
                </div>
                <small>Мин: 50 DAR | Макс: 10,000 DAR</small>
              </div>
              <div className="form-group">
                <label>IBAN вашего банковского счёта</label>
                <input type="text" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="DE89 3704 0044 0532 0130 00" disabled={loading} className={iban && !isValidIban(iban) ? 'input-error' : ''} />
                <small>IBAN счёта компании для получения EUR</small>
              </div>
              {calculation && (
                <div className="calculation">
                  <div className="calc-row"><span>Сумма к конвертации:</span><strong>{calculation.usdt} DAR</strong></div>
                  <div className="calc-row"><span>Курс:</span><strong>{calculation.rate}</strong></div>
                  <div className="calc-row"><span>Спред ({calculation.spread}%):</span><strong>€{calculation.marginEur}</strong></div>
                  <div className="calc-row"><span>Банковская комиссия:</span><strong>€{calculation.bankFee}</strong></div>
                  <div className="calc-row total"><span>Вы получите:</span><strong>€{calculation.finalEur}</strong></div>
                </div>
              )}
            </>
          )}

          {category === 'crypto' && (
            <>
              <div className="info-box"><h4>💎 Обменять DAR → USDT</h4><p>Быстрый вывод криптовалюты на внешний кошелёк</p></div>

              {networks.length > 0 && (
                <div className="form-group">
                  <label>Сеть</label>
                  <div className="network-selector">
                    {networks.map(net => (
                      <button key={net.code} type="button" className={`network-btn ${selectedNetwork === net.code ? 'active' : ''}`} onClick={() => { setSelectedNetwork(net.code); setToAddress(''); }}>
                        <span className="network-name">{net.name}</span>
                        <span className="network-symbol">{net.nativeSymbol}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Сумма вывода</label>
                <div className="input-wrapper">
                  <input type="number" value={amount} onChange={handleAmountChange} placeholder={`${limits.min} - ${limits.max}`} min={limits.min} max={limits.max} step="0.01" disabled={loading} />
                  <span className="currency">USDT</span>
                </div>
                <small>Мин: {limits.min} DAR | Макс: {limits.max.toLocaleString()} DAR | До 100 DAR — авто-одобрение</small>
              </div>

              <div className="form-group">
                <label>{currentNetwork?.name || 'TRON'} адрес получателя</label>
                <input type="text" value={toAddress} onChange={(e) => setToAddress(e.target.value)} placeholder={getAddressPlaceholder()} disabled={loading} className={toAddress && !validateAddress(toAddress) ? 'input-error' : ''} />
                <small>Введите адрес кошелька в сети {currentNetwork?.name || 'TRON'}</small>
              </div>

              {calculation && (
                <div className="calculation">
                  <div className="calc-row"><span>Сумма вывода:</span><strong>{calculation.amount} DAR</strong></div>
                  <div className="calc-row"><span>Комиссия платформы (1%):</span><strong>{calculation.platformFee} DAR</strong></div>
                  <div className="calc-row"><span>Комиссия сети:</span><strong>~{calculation.networkFee} DAR</strong></div>
                  <div className="calc-row total deduction"><span>Будет списано:</span><strong>{calculation.totalDeduction} DAR</strong></div>
                  <div className="calc-row total"><span>Вы получите:</span><strong>{calculation.youReceive} DAR</strong></div>
                </div>
              )}
            </>
          )}

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <button type="submit" disabled={loading || !amount || !calculation || (category === 'crypto' && !validateAddress(toAddress)) || (category === 'bank' && !isValidIban(iban))} className="btn-primary">
            {loading ? '⏳ Создание заявки...' : '📤 Создать заявку на вывод'}
          </button>
        </form>
      )}

      {history.length > 0 && !isGiftCategory && (
        <div className="history-section">
          <h3>📜 История выводов</h3>
          <div className="history-list">
            {history.slice(0, 5).map((wd) => {
              const badge = getStatusBadge(wd.status);
              return (
                <div key={wd.id} className="history-item">
                  <div className="item-main">
                    <span className="method">{wd.method === 'usdt' ? '💎' : '🏦'}</span>
                    <span className="amount">{wd.method === 'usdt' ? `${wd.net_amount} USDT` : `€${wd.net_amount}`}</span>
                    {wd.chain_code && wd.method === 'usdt' && <span className="network-badge">{wd.chain_code.toUpperCase()}</span>}
                    <span className={`badge ${badge.class}`}>{badge.text}</span>
                  </div>
                  <div className="item-info">
                    <span>{wd.to_address ? `→ ${wd.to_address.slice(0,8)}...` : `→ ${wd.iban_to?.slice(0,10)}...`}</span>
                    <span>{new Date(wd.created_at).toLocaleDateString('ru-RU')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessWithdraw;
